import type { Variant } from 'types';
import type { ExperimentWithVerdict } from '@@/api/experiments/implementation-verdict';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useWebsites } from '@/state/useWebsites';
import { useState } from 'react';
import { toast } from 'sonner';
import { offersEndOnImplement } from './offers-end-on-implement';

const QUERY_KEY = 'IMPLEMENTATIONS';

type SetImplementedParams = { experimentId: number; implemented: boolean };

export const useImplementationList = () => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const { website } = useWebsites();
  const websiteId = website?.id || '';
  const [implementedOnly, setImplementedOnly] = useState(false);

  // The page's own UI state lives here with the data it filters, rather than in the
  // components that render it.
  const [searchQuery, setSearchQuery] = useState('');
  const [codeModalVariant, setCodeModalVariant] = useState<Variant | null>(null);

  const { data: experiments, isLoading } = useQuery({
    queryKey: [QUERY_KEY, websiteId, implementedOnly],
    enabled: !!websiteId,
    queryFn: async () => {
      const query = implementedOnly ? '?implementedOnly=true' : '';
      // Each row carries the statistical verdict the page sections on: what won, on
      // which goal, and by how much. See implementation-verdict on the server.
      return apiClient.get<ExperimentWithVerdict[]>(`/experiments/implementations/${websiteId}${query}`);
    },
  });

  // The row whose Implemented tick is waiting on "end it too?". Null when no prompt is open.
  const [endPrompt, setEndPrompt] = useState<ExperimentWithVerdict | null>(null);

  // Sends the state the checkbox is moving to, never a bare flip: see implement-experiment.
  const setImplemented = useMutation({
    mutationFn: async ({ experimentId, implemented }: SetImplementedParams) => {
      return apiClient.post<{ ok: boolean; implementedAt: number | null }>(`/experiments/${experimentId}/implement`, {
        implemented,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: () => {
      toast.error('Failed to update implementation status');
    },
  });

  /*
    Marks first, then ends, as two requests to the endpoints that already exist. The
    mark is what the reader came to do, so it lands even when the end does not, and
    the failure says exactly which half is missing.
  */
  const implementAndEnd = useMutation({
    mutationFn: async (experiment: ExperimentWithVerdict) => {
      await apiClient.post(`/experiments/${experiment.id}/implement`, { implemented: true });
      try {
        await apiClient.post(`/experiments/${experiment.id}/end`);
      } catch (error) {
        const stillLive =
          experiment.status === 'Deployed' ? 'The deployed variant is still being served.' : 'It is still running.';
        throw new Error(`Marked as implemented, but the experiment could not be ended. ${stillLive}`, {
          cause: error,
        });
      }
    },
    onSettled: (_data, _error, experiment) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
      queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experiment.id] });
    },
    onSuccess: () => {
      toast.success('Marked as implemented and ended');
    },
    onError: (error) => {
      console.error(error);
      toast.error(error.cause ? error.message : 'Failed to update implementation status');
    },
  });

  const requestImplemented = (experiment: ExperimentWithVerdict) => {
    if (offersEndOnImplement(experiment)) return setEndPrompt(experiment);
    setImplemented.mutate({ experimentId: experiment.id, implemented: !experiment.implementedAt });
  };

  const resolveEndPrompt = ({ end }: { end: boolean }) => {
    if (!endPrompt) return;
    if (end) implementAndEnd.mutate(endPrompt);
    else setImplemented.mutate({ experimentId: endPrompt.id, implemented: true });
    setEndPrompt(null);
  };

  return {
    experiments: experiments || [],
    isLoading,
    hasWebsite: !!websiteId,
    implementedOnly,
    setImplementedOnly,
    searchQuery,
    setSearchQuery,
    codeModalVariant,
    showCode: setCodeModalVariant,
    hideCode: () => setCodeModalVariant(null),
    requestImplemented,
    endPrompt,
    resolveEndPrompt,
    dismissEndPrompt: () => setEndPrompt(null),
  };
};
